<template>
    <div class="message-search">
        <div class="message-search-content">
            <a-input v-model="searchQuery" />
        </div>
        <div class="search-results">
            <div class="result-item" v-for="item of results" @click="messageSelected(item)">
                <div class="result-avatar">
                    <ContactAvatar v-if="item.messageType === 'visitor'" :contact="contact" :size="'smallest'" />
                    <ContactAvatar
                        v-else-if="item.messageType === 'chatbot'"
                        :fullname="'Chatbot'"
                        :size="'smallest'"
                    />
                    <ContactAvatar v-else-if="!item.user" :fullname="'User'" :size="'smallest'" />
                    <AgentAvatar :userID="item.user._id" :size="35" v-else />
                </div>
                <div class="result-item-content">
                    <div class="user-detail">
                        <div class="author">
                            {{
                                item.messageType === "visitor"
                                    ? contact.fullname
                                    : item.user
                                    ? item.user.name
                                    : item.messageType === "chatbot"
                                    ? "Chatbot"
                                    : "User"
                            }}
                        </div>
                        <div class="date">{{ $moment(item.createdDate).format("HH:mm") }}</div>
                    </div>
                    <div class="message">{{ SanitizeHtml(item.description, true) }}</div>
                </div>
            </div>
        </div>
        <div v-if="!searchQuery.length" class="messages-no-result">
            <div class="material-icons">search</div>
            <span>{{ $t("message.searchPrompt") }}</span>
        </div>
        <div v-else-if="!results.length && searchQuery.length > 0" class="messages-no-result">
            <div class="material-icons">mode_comment</div>
            <span>{{ $t("message.noRecentResults") }}</span>
        </div>

        <SupsisButton @click="loadMore" class="load-more" v-if="searchQuery.length > 0" primary>
            <div class="material-icons">search</div>
            {{ $t("message.loadMore") }}
        </SupsisButton>
    </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import SupsisButton from "../../../views/Pages/Settings/components/SupsisButton.vue";
import AgentAvatar from "../../common/AgentAvatar.vue";
import Fuse from "fuse.js";
import { SanitizeHtml } from "../../../utils/utils";
import ContactAvatar from "./ContactAvatar.vue";

export default {
    name: "MessageSearch",
    components: { ContactAvatar, AgentAvatar, SupsisButton },
    props: {
        contact: {
            type: Object,
            default: () => ({}),
        },
    },
    data: () => ({
        searchQuery: "",
        results: [],
    }),
    computed: {
        ...mapState({
            messagesByContact: (state) => state.contact.messagesByContact,
        }),
    },
    methods: {
        SanitizeHtml,
        ...mapActions({
            loadOldHistoryMessages: "chat/loadOldHistoryMessages",
            listContactMessages: "contact/listContactMessages",
        }),
        search(query) {
            const conversationId = this.$_.get(this.contact, "lastConversation._id");
            const contactId = this.$_.get(this.contact, "_id");
            const bucket = this.messagesByContact[contactId];
            if (!bucket || !bucket.data) return [];
            
            const messages = bucket.data
                .filter((m) => !["system"].includes(m.messageType))
                .map((m) => ({
                    ...m,
                    description: m.description || m.message,
                }));
            const fuse = new Fuse(messages, { includeScore: true, keys: ["description", "user.name"] });
            return fuse.search(query).map((x) => x.item);
        },
        loadMore() {
            const conversationId = this.$_.get(this.contact, "lastConversation._id");
            const contactId = this.$_.get(this.contact, "_id");
            this.listContactMessages({
                contactId,
                conversationId,
                cache: false,
            }).then(() => {
                this.results = this.search(this.searchQuery);
            });
        },
        messageSelected(message) {
            let messageElement = document.getElementById(`sc-message_${message._id}`);
            if (!messageElement) messageElement = document.getElementById(`sc-message_${message.uniqMessageId}`);
            if (messageElement) {
                messageElement.scrollIntoView({ block: "center" });
                messageElement.classList.add("focused-element");
                setTimeout(() => {
                    messageElement.classList.remove("focused-element");
                }, 1000);
            }
        },
    },
    watch: {
        searchQuery() {
            this.results = this.search(this.searchQuery);
        },
    },
};
</script>
<style lang="scss" scoped>
.message-search {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 15px;
    align-items: center;

    .user-info-content-title {
        align-self: flex-start;
    }

    .messages-no-result {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 5px;
        font-size: 18px;
        text-align: center;

        .material-icons {
            font-size: 32px;
        }
    }

    .message-search-content {
        width: 100%;

        input {
            border: 1px solid var(--border-neutral-default);
            padding-right: 7px;
            height: 30px;

            &:hover,
            &:active,
            &:focus {
                box-shadow: none;
            }
        }
    }

    .search-results {
        display: flex;
        flex-direction: column;
        gap: 10px;
        width: 100%;

        .result-item {
            display: flex;
            gap: 4px;
            align-items: flex-start;
            cursor: pointer;
            width: 100%;

            .result-avatar {
                width: 35px;
                height: 35px;
                min-width: 35px;
                min-height: 35px;
            }

            .result-item-content {
                display: flex;
                flex-direction: column;
                gap: 5px;
                overflow: hidden;
                width: 100%;

                .user-detail {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 4px;

                    .author {
                        font-weight: bold;
                        font-size: 16px;
                    }

                    .date {
                        font-size: 12px;
                        font-weight: bold;
                    }
                }

                .message {
                    max-width: 200px;
                    overflow: hidden;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    margin-top: -8px;
                }
            }
        }
    }

    .load-more {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        margin-top: 20px;
        cursor: pointer;

        .material-icons {
            font-size: 18px;
            font-weight: bold;
            margin-right: 10px;
        }
    }
}
</style>
